import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Shield, FileText, FileSignature, X, ExternalLink, CheckCircle2 } from "lucide-react";

/**
 * Overlay bloccante che appare al login quando l'agenzia deve
 * accettare (o riaccettare) i documenti legali VendiDirect (TOS + Privacy + DPA).
 *
 * Uso: montare in App.js dentro il layout autenticato. Se lo stato è must_accept,
 * l'overlay copre l'app finché l'utente non conferma le 3 checkbox.
 */
const DOC_META = {
    TOS: { title: "Termini di Servizio", icon: FileText },
    PRIVACY_VD: { title: "Privacy Policy VendiDirect", icon: Shield },
    DPA: { title: "Accordo sul Trattamento dei Dati (DPA)", icon: FileSignature },
};

export default function LegalAcceptanceGate() {
    const { user } = useAuth();
    const [state, setState] = useState(null);
    const [documents, setDocuments] = useState(null);
    const [previewing, setPreviewing] = useState(null); // doc_type
    const [checks, setChecks] = useState({ TOS: false, PRIVACY_VD: false, DPA: false });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const [st, docs] = await Promise.all([
                api.get("/legal/status"),
                api.get("/legal/documents/current"),
            ]);
            setState(st.data);
            setDocuments(docs.data.documents);
            // Le checkbox partono TUTTE non-spuntate (richiesto: nessuna pre-spunta)
            const initial = {};
            (st.data.pending || []).forEach((p) => { initial[p.doc_type] = false; });
            setChecks(initial);
        } catch { /* silent */ }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    if (!user || !state || !state.applicable || !state.must_accept) return null;

    // Mostra SOLO i documenti che richiedono azione (nuovi utenti = tutti e 3;
    // utenti con singola versione aggiornata = solo quel documento).
    const pendingList = state.pending || [];
    const pendingTypes = pendingList.map((p) => p.doc_type);
    const allChecked = pendingTypes.every((t) => checks[t]);

    const submit = async () => {
        if (!allChecked) { setErr("Devi accettare tutti e tre i documenti per continuare."); return; }
        setSaving(true); setErr("");
        try {
            const acceptances = (state.pending || []).map((p) => ({ doc_type: p.doc_type, version: p.current_version }));
            await api.post("/legal/accept", { acceptances, source: "login_gate" });
            await load();
            // Se dopo il reload lo status è a posto, si smonta automaticamente
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="legal-gate" className="fixed inset-0 z-[1100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-2xl max-h-[90vh] border border-gold/40 bg-ink-950 flex flex-col">
                <div className="border-b border-white/10 px-6 py-5">
                    <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2">
                        <Shield size={13} /> Prima di continuare
                    </div>
                    <h2 className="font-serif text-2xl text-ink-50 mt-2">Documenti legali VendiDirect</h2>
                    <p className="text-xs text-ink-400 mt-2">
                        {state.accepted?.length === 0
                            ? "Per attivare il gestionale, ti chiediamo di leggere e accettare i seguenti documenti."
                            : `Abbiamo aggiornato ${pendingList.length === 1 ? "un documento legale" : `${pendingList.length} documenti legali`}. Ti chiediamo di rileggerlo e riaccettarlo per continuare.`}
                    </p>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-3">
                    {pendingList.map((p) => {
                        const dt = p.doc_type;
                        const meta = DOC_META[dt];
                        const Icon = meta.icon;
                        const doc = documents?.[dt] || {};
                        const isOutdated = p.status === "outdated";
                        return (
                            <div
                                key={dt}
                                data-testid={`legal-row-${dt}`}
                                className="border border-white/15 bg-ink-900/40 p-4"
                            >
                                <div className="flex items-start gap-3">
                                    <label className="flex items-start gap-3 flex-1 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid={`legal-check-${dt}`}
                                            checked={!!checks[dt]}
                                            onChange={(e) => setChecks({ ...checks, [dt]: e.target.checked })}
                                            className="mt-1 h-4 w-4 accent-gold shrink-0"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 text-sm text-ink-50 flex-wrap">
                                                <Icon size={14} className="text-gold" />
                                                <span>Ho letto e accetto i <strong>{meta.title}</strong></span>
                                                {isOutdated && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-yellow-700/40 bg-yellow-900/20 text-yellow-300">Aggiornato</span>}
                                            </div>
                                            <div className="text-[11px] text-ink-400 mt-1">
                                                Versione corrente: {doc.version || p.current_version || "—"}
                                                {isOutdated && p.accepted_version && (
                                                    <span className="ml-2 text-ink-500">(precedente: {p.accepted_version})</span>
                                                )}
                                                {doc.is_placeholder && <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-white/15 text-ink-400">Placeholder</span>}
                                            </div>
                                        </div>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setPreviewing(dt)}
                                        data-testid={`legal-preview-${dt}`}
                                        className="text-gold hover:text-gold/80 text-xs flex items-center gap-1 shrink-0"
                                    >
                                        Leggi <ExternalLink size={11} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {err && <div data-testid="legal-error" className="text-xs text-red-300 border border-red-700/40 bg-red-900/20 px-3 py-2">{err}</div>}
                </div>

                <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between">
                    <div className="text-[10px] text-ink-500">
                        Registriamo data, ora, versione e indirizzo IP per ogni accettazione (art. 7 GDPR).
                    </div>
                    <button
                        onClick={submit}
                        disabled={!allChecked || saving}
                        data-testid="legal-submit"
                        className="cta-gold px-6 py-2.5 text-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <CheckCircle2 size={13} /> {saving ? "Salvataggio..." : "Accetto e continuo"}
                    </button>
                </div>
            </div>

            {previewing && (
                <LegalDocOverlay docType={previewing} document={documents?.[previewing]} onClose={() => setPreviewing(null)} />
            )}
        </div>
    );
}

export function LegalDocOverlay({ docType, document: doc, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    if (!doc) return null;
    const meta = DOC_META[docType] || { title: doc.label };
    return (
        <div data-testid="legal-doc-overlay" className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="relative w-full max-w-3xl max-h-[85vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div>
                        <div className="font-serif text-lg text-ink-50">{meta.title}</div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-400">Versione {doc.version}</div>
                    </div>
                    <button onClick={onClose} data-testid="legal-doc-close" className="text-ink-400 hover:text-ink-50 border border-white/10 p-1.5" aria-label="Chiudi">
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {doc.has_pdf ? (
                        <div className="h-[65vh]">
                            <iframe title={meta.title} src={`${process.env.REACT_APP_BACKEND_URL}${doc.pdf_url}`} className="w-full h-full border border-white/10 bg-white" />
                        </div>
                    ) : (
                        <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-ink-100">{doc.content_text || "—"}</pre>
                    )}
                </div>
            </div>
        </div>
    );
}
